import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import {
  buildDischargeHandoffUrl,
  subscribeDischargeHandoff,
} from "services/healthRecordsShareService";


/**
 * "Send to patient" for an encounter with no imported record.
 *
 * The provider has no uid to send to, so the patient's own phone collects the
 * instructions: this shows a one-time QR, the phone claims it, and the handoff doc
 * flips to `claimed` (see functions/src/accounts/dischargeHandoff.js).
 *
 * ⚠️ THIS DIALOG ONLY EVER SHOWS A CODE, same as PhoneRecordingDialog. The moment the
 * patient's phone claims it, the parent closes this and says so with an inline
 * success Alert. The code is spent by then, and a modal that has stopped asking for
 * anything is just something else to close.
 *
 * ⚠️ EVERY OPENING MINTS A FRESH CODE, from whatever instructions are on screen now.
 * That is what "Make a new code" and "ask your provider for a new one" rely on: a
 * code that expired, or was scanned by the wrong phone, is replaced by opening this
 * again, with no new encounter.
 */
const DischargeHandoffDialog = ({ open, onClose, onCreateHandoff, onClaimed }) => {
  const [handoff, setHandoff] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  // Guards against a slow mint landing after the dialog was closed or re-minted.
  const requestRef = useRef(0);

  const mint = useCallback(async () => {
    const request = requestRef.current + 1;
    requestRef.current = request;
    setCreating(true);
    setError("");
    setExpired(false);
    setHandoff(null);
    try {
      const result = await onCreateHandoff?.();
      if (requestRef.current !== request) return;
      setHandoff(result);
    } catch (err) {
      if (requestRef.current !== request) return;
      setError(err?.message || "Could not prepare the code for the patient.");
    } finally {
      if (requestRef.current === request) setCreating(false);
    }
  }, [onCreateHandoff]);

  useEffect(() => {
    if (open) {
      void mint();
      return undefined;
    }
    requestRef.current += 1;
    setHandoff(null);
    setError("");
    setExpired(false);
    setCreating(false);
    return undefined;
    // Minting is keyed on opening only. Re-running on every new onCreateHandoff
    // identity would swap the code under a camera already pointed at it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handoffId = handoff?.handoffId || null;

  useEffect(() => {
    if (!open || !handoffId) return undefined;
    return subscribeDischargeHandoff(
      handoffId,
      (doc) => {
        if (doc?.status === "claimed") onClaimed?.();
      },
      (err) => console.warn("[DischargeHandoffDialog] handoff watch failed", err),
    );
  }, [open, handoffId, onClaimed]);

  // The server refuses an expired code anyway; this just stops the screen showing
  // one that no longer works.
  useEffect(() => {
    if (!open || !handoff?.expiresAt) return undefined;
    const remaining = handoff.expiresAt - Date.now();
    if (remaining <= 0) {
      setExpired(true);
      return undefined;
    }
    const timer = setTimeout(() => setExpired(true), remaining);
    return () => clearTimeout(timer);
  }, [open, handoff?.expiresAt]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send discharge instructions</DialogTitle>
      <DialogContent>
        {error ? (
          <Stack spacing={2}>
            <Alert severity="error">{error}</Alert>
            <Button onClick={() => void mint()}>Try again</Button>
          </Stack>
        ) : expired ? (
          <Stack spacing={2}>
            <Alert severity="info">This code has expired.</Alert>
            <Button onClick={() => void mint()}>Make a new code</Button>
          </Stack>
        ) : creating || !handoffId ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Typography variant="body2">
              Please ask the patient to scan this code with their phone camera.
            </Typography>

            <Box
              sx={{
                display: "flex",
                // Hug the code rather than fill the width: the white is the quiet zone, not a band.
                width: "fit-content",
                mx: "auto",
                p: 2,
                // Stays white in dark mode: scanners need dark-on-light with a quiet zone
                bgcolor: "common.white",
                borderRadius: 1,
              }}
            >
              <QRCodeSVG value={buildDischargeHandoffUrl(handoffId)} size={220} />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
              This code works once and expires in an hour.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DischargeHandoffDialog;
